import { Formik, Form } from 'formik'
import React, { useEffect, useState } from 'react'
import { FiCheck } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { useMutation, useQuery } from '@tanstack/react-query'

import Yup from '../../../services/yup'
import { Button, Select, Textarea, Textbox } from '../../../components/Form'
import { queryClient } from '../../../contexts/ReactQueryContext'
import formUtils from '../../../util/form'
import { api, authHeaders } from '../../../services/api'
import masks from '../../../util/masks'
import Spinner from '../../../components/Spinner'

const baseInitialValues = {
	titulo: '',
	descricao: '',
	inicio_data: '',
	inicio_hora: '',
	fim_data: '',
	fim_hora: '',
	tipo: null
}

export default function ({ lembrete, onSubmit }) {
	const [initialValues, setInitialValues] = useState(baseInitialValues)

	const { data: tiposLembrete } = useQuery({
		queryKey: ['obter-tipos-lembretes'],
		queryFn: async () => {
			const response = await api.get('tipo_lembrete', authHeaders())

			return response
		}
	})
	
	const { mutate: handleAddLembrete } = useMutation({
		mutationFn: async data => {
			try {
				data = formUtils.extractFormValues(data, false)

				await api.post('lembrete', data, authHeaders())

				toast.success('Lembrete cadastrado com sucesso!')

				onSubmit?.()
			} catch(e) {
				toast.error(e.msg)
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ 
				queryKey: ['obter-lista-lembretes', 'obter-lembretes-por-id'] 
			})
		}
	})

	const { mutate: handleUpdateLembrete } = useMutation({
		mutationFn: async data => {
			try {
				data = formUtils.extractFormValues(data, false)

				await api.put(`lembrete/${lembrete.id}`, data, authHeaders())

				toast.success('Lembrete atualizado com sucesso!')

				onSubmit?.()
			} catch(e) {
				toast.error(e.msg)
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['obter-lista-lembretes'] })
		}
	})

	useEffect(() => {
		if(lembrete && tiposLembrete) {
			setInitialValues({
				titulo: lembrete.titulo,
				descricao: lembrete.descricao,
				inicio_data: format(new Date(lembrete.inicio), 'dd/MM/yyyy'),
				inicio_hora: format(new Date(lembrete.inicio), 'HH:mm'),
				fim_data: format(new Date(lembrete.fim), 'dd/MM/yyyy'),
				fim_hora: format(new Date(lembrete.fim), 'HH:mm'),
				tipo: {
					label: lembrete.tipo.descricao,
					value: lembrete.tipo.id
				}
			})
		}
	}, [lembrete, tiposLembrete])

	if(!tiposLembrete) {
		return <Spinner />
	}

	return (
		<Formik
			onSubmit={lembrete ? handleUpdateLembrete : handleAddLembrete}
			initialValues={initialValues}
			enableReinitialize
			validationSchema={Yup.object({
				titulo: Yup.string().required('Informe um nome para o lembrete.'),
				descricao: Yup.string(),
				inicio_data: Yup.string().required('Informe a data inicial.')
					.matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Use o formato: dd/MM/yyyy.'),
				inicio_hora: Yup.string().required('Informe a hora inicial.')
					.matches(/^\d{2}:\d{2}$/, 'Use o formato: hh:mm.'),
				fim_data: Yup.string().required('Informe a data final.')
					.matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Use o formato: dd/MM/yyyy'),
				fim_hora: Yup.string().required('Informe a hora final.')
					.matches(/^\d{2}:\d{2}$/, 'Use o formato: hh:mm.')
			})}
		>
			{({ setFieldValue }) => (
				<Form className="mx-2 flex flex-col gap-4">
					<div className="grid grid-cols-2">
						<Select
							name="tipo"
							label="Tipo de lembrete"
							options={tiposLembrete.map(item => ({
								label: item.descricao,
								value: item.id
							}))}
							onChange={(selected, meta) => {
								setFieldValue(meta.name, selected)
							}}
							containerClass="w-36"
						/>

						<Textbox
							label="Nome do lembrete"
							name="titulo"
						/>

						<Textarea
							label="Detalhes do lembrete"
							name="descricao"
							containerClass="col-span-2"
						/>

						<Textbox
							label="Data de início"
							name="inicio_data"
							mask={masks.date}
						/>

						<Textbox
							label="Horário de início"
							name="inicio_hora"
							mask={masks.time}
						/>

						<Textbox
							label="Data de fim"
							name="fim_data"
							mask={masks.date}
						/>

						<Textbox
							label="Horário de fim"
							name="fim_hora"
							mask={masks.time}
						/>
					</div>

					<Button type="submit" className="self-end w-fit">
						Cadastrar
						<FiCheck size={16} />
					</Button>
				</Form>
			)}
		</Formik>
	)
}
